import { useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { Reorder, useDragControls } from 'framer-motion'
import { FiChevronLeft } from 'react-icons/fi'
import { CiSearch } from 'react-icons/ci'
import { IoClose } from 'react-icons/io5'
import { MdDragIndicator } from 'react-icons/md'
import { faPlain, fundName, fundTitle } from 'utilities/helper/format'
import {
    applySavedOrder,
    matchesQuery,
    readSavedOrder,
    saveOrder,
} from './managedFundsOrder'

const VISIBLE = 3

/**
 * Below this many funds a search box is noise — you can see every row at once,
 * and a text field above three items reads as clutter rather than as help.
 */
const SEARCH_FROM = 5

/**
 * How long the typing has to stop before the search result is reported.
 *
 * Long enough that «رض» on the way to «رضایی» is not counted as a search that
 * found nothing.
 */
const SEARCH_SETTLE_MS = 400

/**
 * «صندوق‌هایی که مدیر آن هستید» — a door to داشبورد مدیریت.
 *
 * Navigation only, derived client-side from the already cached `['get-boxes']`
 * payload, which carries `poolship.role` for every fund. The work signals (how
 * many members are behind, how many loan requests are waiting) need the new
 * `managed-summary` endpoint.
 *
 * Hard rule, whatever ships later: never call `admin-dashboard/stats/` per
 * managed fund from here. It runs ~4s on the largest fund, so a manager of
 * three funds would 504 on every app open.
 *
 * Managers of many funds could neither reorder nor find one (hamyan-pwa#12),
 * so the rows now carry a drag handle and the list gets a search box once it
 * is long enough to need one. The order is persisted per browser only — see
 * `managedFundsOrder.ts` for why, and for the merge rules.
 *
 * Two things the layout depends on, both easy to break:
 *
 * - The collapsed list is a **prefix** of the effective order. `onReorder`
 *   only hears about the rows on screen, and it reattaches the untouched tail
 *   by position; a non-prefix slice would silently scramble the hidden funds.
 *   That is also why dragging is off while searching, where the visible rows
 *   are a filtered subset rather than a prefix.
 * - The handle is the row's first DOM child. The page is `direction: rtl`, so
 *   first child renders at the **right** edge, which is where the issue asks
 *   for it — «سمت راست اسم صندوق».
 */
export default function ManagedFunds({
    moneyPools,
}: {
    moneyPools: MoneyPool[]
}) {
    const navigate = useNavigate()
    const [expanded, setExpanded] = useState(false)
    const [query, setQuery] = useState('')
    const [savedIds, setSavedIds] = useState<number[]>(readSavedOrder)

    // The live order and where the current drag picked its row up from.
    // `onReorder` runs on every intermediate swap, so neither can be read off a
    // closure captured when the gesture started — see `onDragEnd`.
    const orderRef = useRef<number[]>([])
    const dragFrom = useRef<number | null>(null)

    const managed = useMemo(
        () =>
            applySavedOrder(
                moneyPools.filter((mp) =>
                    ['own', 'mgr'].includes(mp.poolship?.role)
                ),
                savedIds
            ),
        [moneyPools, savedIds]
    )

    const searching = query.trim().length > 0
    const matches = useMemo(
        () =>
            searching
                ? managed.filter((mp) => matchesQuery(mp, query))
                : managed,
        [managed, query, searching]
    )

    orderRef.current = managed.map((mp) => mp.id)

    // Search had an entry event and no outcome, so «found nothing» — the case
    // that decides whether the box is worth keeping — was invisible. Debounced
    // rather than per-keystroke for the same reason `managed_funds_search_used`
    // fires once: one event per attempt, not per character.
    useEffect(() => {
        if (!searching) return
        const timer = window.setTimeout(() => {
            posthog.capture('managed_funds_search_result', {
                funds_count: managed.length,
                result_count: matches.length,
                is_empty: matches.length === 0,
            })
        }, SEARCH_SETTLE_MS)
        return () => window.clearTimeout(timer)
        // Keyed on the query and the size of its answer; `matches` itself is a
        // new array on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, searching, matches.length, managed.length])

    if (managed.length === 0) return null

    const sortable = !searching && managed.length > 1
    const shown = searching || expanded ? matches : matches.slice(0, VISIBLE)
    const hidden = searching ? 0 : managed.length - shown.length

    const persist = (next: MoneyPool[]) => {
        const ids = next.map((mp) => mp.id)
        saveOrder(ids)
        setSavedIds(ids)
    }

    /**
     * Reorder hands back only the ids currently rendered. Because the
     * collapsed view is a prefix of `managed`, the rest can be appended
     * unchanged — which also means the *whole* list is written on the first
     * drag, so a fund below the fold is never mistaken for a new one later.
     */
    const onReorder = (ids: number[]) => {
        const byId = new Map(managed.map((mp) => [mp.id, mp]))
        const head = ids
            .map((id) => byId.get(id))
            .filter((mp): mp is MoneyPool => !!mp)
        persist([...head, ...managed.slice(head.length)])
    }

    /**
     * One drag is one event.
     *
     * `onReorder` is not the gesture — framer-motion fires it once per
     * intermediate swap, so a row dragged from fourth place to first reported
     * three reorders. The counter looked like heavy use of a feature nobody had
     * touched three times. The gesture's own start and end are the honest
     * boundary, and the distance between them is what the swaps were really
     * describing.
     */
    const onDragStart = (id: number) => {
        dragFrom.current = orderRef.current.indexOf(id)
    }

    const onDragEnd = (id: number) => {
        const from = dragFrom.current
        dragFrom.current = null
        const to = orderRef.current.indexOf(id)
        // Picked up and put back down: no order changed, so nothing to report.
        if (from === null || from < 0 || to < 0 || to === from) return
        posthog.capture('managed_funds_reordered', {
            method: 'drag',
            funds_count: managed.length,
            moved_distance: Math.abs(to - from),
        })
    }

    /** Keyboard path for the same thing — framer-motion's drag has none. */
    const move = (id: number, delta: number) => {
        const from = managed.findIndex((mp) => mp.id === id)
        const to = from + delta
        if (from < 0 || to < 0 || to >= managed.length) return
        const next = [...managed]
        next.splice(to, 0, next.splice(from, 1)[0])
        persist(next)
        posthog.capture('managed_funds_reordered', {
            method: 'keyboard',
            funds_count: managed.length,
            moved_distance: Math.abs(delta),
        })
    }

    // Once per search, on the empty → non-empty edge. One event per keystroke
    // would drown the funnel.
    const onQueryChange = (value: string) => {
        if (!searching && value.trim()) {
            posthog.capture('managed_funds_search_used', {
                funds_count: managed.length,
            })
        }
        setQuery(value)
    }

    const open = (mp: MoneyPool, rowIndex: number) => {
        // `from_search` makes this search's success event: a manager who types
        // a name and then opens a row got what they came for, and no separate
        // event can observe that.
        posthog.capture('admin_dashboard_opened', {
            source: 'home',
            moneypool_id: mp.id,
            from_search: searching,
            row_index: rowIndex,
        })
        navigate(`/fund/admin-dashboard/${mp.id}`)
    }

    return (
        <div>
            <p className="text-sm font-bold text-text-secondary text-right mb-2 px-1">
                صندوق‌هایی که مدیر آن هستید
            </p>
            <div className="bg-white border border-divider rounded-card overflow-hidden">
                {managed.length >= SEARCH_FROM && (
                    <div className="px-3 py-2 border-b border-divider">
                        <div className="flex items-center gap-2 rounded-xl bg-canvas px-3 py-2">
                            <CiSearch
                                size={18}
                                className="shrink-0 text-text-muted"
                            />
                            <input
                                value={query}
                                onChange={(e) => onQueryChange(e.target.value)}
                                placeholder="جستجوی نام صندوق"
                                aria-label="جستجوی نام صندوق"
                                className="w-full bg-transparent text-xs outline-none placeholder:text-text-muted"
                            />
                            {searching && (
                                <button
                                    type="button"
                                    onClick={() => setQuery('')}
                                    aria-label="پاک کردن جستجو"
                                    className="shrink-0 cursor-pointer"
                                >
                                    <IoClose className="text-text-muted" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <Reorder.Group
                    as="div"
                    axis="y"
                    values={shown.map((mp) => mp.id)}
                    onReorder={onReorder}
                >
                    {shown.map((mp, i) => (
                        <FundRow
                            key={mp.id}
                            fund={mp}
                            divided={i > 0}
                            sortable={sortable}
                            onOpen={() => open(mp, i)}
                            onMove={(delta) => move(mp.id, delta)}
                            onDragStart={() => onDragStart(mp.id)}
                            onDragEnd={() => onDragEnd(mp.id)}
                        />
                    ))}
                </Reorder.Group>

                {searching && shown.length === 0 && (
                    <p className="px-4 py-4 text-xs text-text-secondary text-right">
                        صندوقی با این نام پیدا نشد.
                    </p>
                )}

                {hidden > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            // How often three visible rows are not enough —
                            // i.e. whether VISIBLE is set right.
                            posthog.capture('managed_funds_expanded', {
                                funds_count: managed.length,
                                hidden_count: hidden,
                            })
                            setExpanded(true)
                        }}
                        className="w-full border-t border-divider py-2 text-xs text-green-1 font-bold cursor-pointer"
                    >
                        {faPlain.format(hidden)} صندوق دیگر
                    </button>
                )}
            </div>

            {/* Self-dismissing: the handle is a new affordance and needs to be
                named once, but a permanent caption under the list would be
                clutter. The first drag writes an order, and the hint is gone
                for good. */}
            {sortable && savedIds.length === 0 && (
                <p className="mt-1 px-1 text-[11px] text-text-muted text-right">
                    با کشیدن دستگیرهٔ کنار هر صندوق، ترتیب فهرست را تغییر دهید.
                </p>
            )}
        </div>
    )
}

function FundRow({
    fund,
    divided,
    sortable,
    onOpen,
    onMove,
    onDragStart,
    onDragEnd,
}: {
    fund: MoneyPool
    divided: boolean
    sortable: boolean
    onOpen: () => void
    onMove: (delta: number) => void
    onDragStart: () => void
    onDragEnd: () => void
}) {
    const controls = useDragControls()

    return (
        <Reorder.Item
            as="div"
            value={fund.id}
            dragListener={false}
            dragControls={controls}
            // Drag lifecycle, not `onReorder`: these fire once per gesture even
            // though the list reorders several times inside one.
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            whileDrag={{
                scale: 1.02,
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
            }}
            // Opaque on purpose: a dragged row floats over its neighbours.
            className={`bg-white flex items-center ${
                divided ? 'border-t border-divider' : ''
            }`}
        >
            {sortable && (
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={`تغییر ترتیب ${fundTitle(fund.name)}`}
                    onPointerDown={(e) => controls.start(e)}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowUp') {
                            e.preventDefault()
                            onMove(-1)
                        }
                        if (e.key === 'ArrowDown') {
                            e.preventDefault()
                            onMove(1)
                        }
                    }}
                    // `touch-none` is load-bearing: without it the browser
                    // claims the gesture and scrolls the page instead.
                    className="shrink-0 touch-none cursor-grab active:cursor-grabbing pr-4 pl-2 py-3 text-text-muted"
                >
                    <MdDragIndicator size={18} />
                </span>
            )}
            <button
                type="button"
                onClick={onOpen}
                className={`flex-1 min-w-0 flex items-center gap-2 py-3 pl-4 text-right cursor-pointer ${
                    sortable ? '' : 'pr-4'
                }`}
            >
                <span className="flex-1 min-w-0">
                    <span className="block text-sm text-heading font-bold line-clamp-2 break-words">
                        {fundName(fund.name)}
                    </span>
                    <span className="block text-xs text-text-secondary">
                        {faPlain.format(fund.active_members_count ?? 0)} عضو
                        {fund.poolship?.role === 'own' && ' · مالک'}
                    </span>
                </span>
                <FiChevronLeft className="text-text-muted shrink-0" />
            </button>
        </Reorder.Item>
    )
}
